import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  CopyButton,
  FullscreenButton,
  HistoryPanel,
  Panel,
  ResetButton,
  SoundToggle,
  usePersistentState,
  useSfx,
} from "@/components/tools/ToolKit";
import { randomInt } from "@/lib/random-engine";
import { Dices, Lock, Unlock, RotateCcw } from "lucide-react";

export const DICE_TYPES = [4, 6, 8, 10, 12, 20, 100] as const;
export type DiceType = (typeof DICE_TYPES)[number];

interface DieState {
  id: string;
  sides: DiceType;
  value: number;
  locked: boolean;
}

// Render real face dots (pips) for 6-sided die
function D6Pips({ value }: { value: number }) {
  const pipsMap: Record<number, number[]> = {
    1: [4],
    2: [0, 8],
    3: [0, 4, 8],
    4: [0, 2, 6, 8],
    5: [0, 2, 4, 6, 8],
    6: [0, 2, 3, 5, 6, 8],
  };
  const active = pipsMap[value] || [4];

  return (
    <div className="grid size-full grid-cols-3 grid-rows-3 gap-1.5 p-3 place-items-center">
      {Array.from({ length: 9 }).map((_, i) => (
        <div key={i} className="flex size-full items-center justify-center">
          {active.includes(i) && (
            <span className="size-3.5 sm:size-4 rounded-full bg-slate-900 shadow-inner dark:bg-white" />
          )}
        </div>
      ))}
    </div>
  );
}

export function DiceRoller() {
  const [sides, setSides] = useState<DiceType>(6);
  const [count, setCount] = useState(2);
  const [rolling, setRolling] = useState(false);
  const [dice, setDice] = useState<DieState[]>([]);
  const [history, setHistory] = useState<string[]>([]);
  const [soundOn, setSoundOn] = usePersistentState("pickamini:dice:sound", true);
  const stageRef = useRef<HTMLDivElement>(null);
  const sfx = useSfx(soundOn);

  // Sync dice list when count or sides change
  useEffect(() => {
    setDice((prev) => {
      if (prev.length === count && prev.every((d) => d.sides === sides)) return prev;
      return Array.from({ length: count }, (_, i) => ({
        id: `die-${i}-${Date.now()}`,
        sides,
        value: prev[i] ? prev[i]!.value : 1 + randomInt(sides),
        locked: false,
      }));
    });
  }, [count, sides]);

  const roll = () => {
    if (rolling) return;
    setRolling(true);
    sfx.diceRattle();

    const startTime = Date.now();
    const duration = 600;

    const iv = window.setInterval(() => {
      setDice((prev) =>
        prev.map((d) => (d.locked ? d : { ...d, value: 1 + randomInt(d.sides) })),
      );

      if (Date.now() - startTime >= duration) {
        window.clearInterval(iv);
        const finalDice = dice.map((d) =>
          d.locked ? d : { ...d, value: 1 + randomInt(d.sides) },
        );
        setDice(finalDice);
        setRolling(false);
        sfx.diceLand();

        const vals = finalDice.map((d) => d.value);
        const sum = vals.reduce((a, b) => a + b, 0);
        setHistory((h) =>
          [`${count}d${sides}: ${vals.join(", ")} (Total: ${sum})`, ...h].slice(0, 50),
        );
      }
    }, 60);
  };

  const rollSingle = (index: number) => {
    if (rolling || dice[index]?.locked) return;
    sfx.click();
    const val = 1 + randomInt(dice[index]!.sides);
    setDice((prev) => prev.map((d, i) => (i === index ? { ...d, value: val } : d)));
  };

  const toggleLock = (index: number) => {
    sfx.click();
    setDice((prev) =>
      prev.map((d, i) => (i === index ? { ...d, locked: !d.locked } : d)),
    );
  };

  const total = dice.reduce((acc, d) => acc + d.value, 0);

  return (
    <div className="grid gap-5 lg:grid-cols-[320px_1fr_300px]">
      {/* Settings Panel */}
      <Panel title="Dice Setup" className="no-print space-y-4">
        <div>
          <p className="mb-2 text-sm font-medium">Die Type</p>
          <div className="flex flex-wrap gap-2">
            {DICE_TYPES.map((d) => (
              <Button
                key={d}
                size="sm"
                variant={sides === d ? "default" : "outline"}
                className="font-semibold"
                onClick={() => setSides(d)}
              >
                D{d}
              </Button>
            ))}
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium">Number of Dice</label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={1}
              max={12}
              value={count}
              onChange={(e) => setCount(Math.min(12, Math.max(1, Number(e.target.value))))}
              className="mt-1"
            />
            <div className="flex gap-1 pt-1">
              {[1, 2, 3, 5].map((n) => (
                <Button
                  key={n}
                  size="sm"
                  variant="outline"
                  className="px-2.5 text-xs"
                  onClick={() => setCount(n)}
                >
                  {n}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <SoundToggle on={soundOn} onToggle={() => setSoundOn(!soundOn)} />
          <ResetButton
            onClick={() => {
              setCount(2);
              setSides(6);
              setHistory([]);
              setDice((prev) => prev.map((d) => ({ ...d, locked: false })));
            }}
          />
        </div>
      </Panel>

      {/* Main Dice Stage */}
      <div
        ref={stageRef}
        className="surface-card relative flex flex-col items-center justify-between gap-6 p-8 min-h-[360px]"
      >
        <div className="w-full flex items-center justify-between no-print">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Dices className="size-4 text-primary" />
            <span>
              {count} x D{sides}
            </span>
          </div>
          <FullscreenButton targetRef={stageRef} />
        </div>

        {/* Dice Cards Grid */}
        <div className="flex flex-wrap items-center justify-center gap-4 my-auto py-4">
          {dice.map((die, idx) => (
            <div
              key={die.id}
              onClick={() => rollSingle(idx)}
              className={`group relative flex size-24 sm:size-28 cursor-pointer select-none items-center justify-center rounded-2xl border-2 bg-card p-2 shadow-lift transition-all hover:scale-105 ${
                die.locked ? "border-amber-500 ring-2 ring-amber-500/50" : "border-border"
              } ${rolling && !die.locked ? "animate-dice" : "animate-reveal"}`}
            >
              {/* Lock Toggle Button */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleLock(idx);
                }}
                className={`absolute -top-2 -right-2 z-10 flex size-7 items-center justify-center rounded-full border shadow-sm transition-all ${
                  die.locked
                    ? "border-amber-400 bg-amber-500 text-slate-950 scale-110"
                    : "border-border bg-secondary text-muted-foreground hover:scale-105 opacity-0 group-hover:opacity-100"
                }`}
                title={die.locked ? "Unlock die" : "Lock die"}
              >
                {die.locked ? <Lock className="size-3.5 stroke-[2.5]" /> : <Unlock className="size-3.5" />}
              </button>

              {/* Die Content: D6 Pips vs Polyhedral Numbers */}
              {die.sides === 6 ? (
                <D6Pips value={die.value} />
              ) : (
                <div className="flex flex-col items-center justify-center text-center">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    D{die.sides}
                  </span>
                  <span className="font-display text-4xl font-extrabold text-foreground">
                    {die.value}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Total & Roll Action */}
        <div className="flex flex-col items-center gap-3 w-full">
          <p className="font-display text-2xl font-bold text-foreground">
            Total: <span className="text-primary font-extrabold">{total}</span>
          </p>

          <div className="flex items-center gap-2">
            <Button
              size="lg"
              className="h-14 px-10 text-lg font-bold"
              disabled={rolling}
              onClick={roll}
            >
              <RotateCcw className={`size-5 mr-2 ${rolling ? "animate-spin" : ""}`} />
              {rolling ? "Rolling…" : "Roll Dice"}
            </Button>

            {dice.some((d) => d.locked) && (
              <Button
                variant="outline"
                size="lg"
                onClick={() => setDice((prev) => prev.map((d) => ({ ...d, locked: false })))}
                className="h-14 px-4 text-sm"
              >
                <Unlock className="size-4 mr-1.5" /> Unlock All
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* History Panel */}
      <HistoryPanel items={history} onClear={() => setHistory([])} title="Roll History" />
    </div>
  );
}
